import { Link } from 'react-router-dom'
import styled from 'styled-components'

export const Page = styled.main`
  width: 100%;
  min-height: 100vh;
  background: #f8f8f7;
  color: #333;
`

export const Hero = styled.section`
  width: 100%;
  padding: 52px 24px 38px;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;

  @media (max-width: 720px) {
    padding: 38px 16px 30px;
  }
`

export const HeroContent = styled.div`
  width: min(1240px, 100%);
  margin: 0 auto;

  h1 {
    max-width: 760px;
    margin: 8px 0 14px;
    color: #292929;
    font-size: clamp(34px, 4vw, 50px);
    line-height: 1.06;
    letter-spacing: -0.02em;
  }

  p {
    max-width: 760px;
    margin: 0;
    color: #666666;
    font-size: 17px;
    line-height: 1.65;
  }

  @media (max-width: 720px) {
    h1 {
      font-size: 36px;
    }

    p {
      font-size: 15px;
    }
  }
`

export const Eyebrow = styled.span`
  display: block;
  margin-bottom: 8px;
  color: #e00000;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

export const Section = styled.section`
  width: min(1240px, calc(100% - 48px));
  margin: 0 auto;
  padding: 46px 0;

  @media (max-width: 720px) {
    width: calc(100% - 28px);
    padding: 34px 0;
  }
`

export const SectionHeader = styled.header`
  margin-bottom: 22px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 24px;

  h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #171b24;
    font-size: 26px;
  }

  h2 svg {
    color: #e90000;
    font-size: 20px;
  }

  p {
    margin: 6px 0 0;
    color: #737b88;
    font-size: 13px;
  }

  @media (max-width: 720px) {
    margin-bottom: 16px;
    align-items: flex-start;

    h2 {
      font-size: 20px;
    }
  }

  @media (max-width: 620px) {
    flex-direction: column;
    gap: 10px;
  }
`

export const SectionAction = styled(Link)`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #e90000;
  font-size: 12px;
  font-weight: 800;

  &:hover {
    color: #c80000;
  }

  @media (max-width: 620px) {
    margin-top: 4px;
    font-size: 11px;
  }
`

export const CarouselShell = styled.div`
  position: relative;
`

export const CarouselViewport = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const CarouselItem = styled.div`
  flex: 0 0 calc((100% - 32px) / 3);
  min-width: 0;
  scroll-snap-align: start;

  > * {
    height: 100%;
  }

  @media (max-width: 980px) {
    flex-basis: calc((100% - 16px) / 2);
  }

  @media (max-width: 620px) {
    flex-basis: 86%;
  }
`

export const CarouselButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ({ $side }) => ($side === 'left' ? 'left: -18px;' : 'right: -18px;')
  z-index: 2;
  width: 38px;
  height: 38px;
  border: 1px solid #e4e6e9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #303744;
  box-shadow: 0 7px 22px rgba(20, 24, 31, 0.12);
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    color: #e90000;
  }

  @media (max-width: 720px) {
    width: 34px;
    height: 34px;
    ({ $side }) => ($side === 'left' ? 'left: 4px;' : 'right: 4px;')
  }
`

export const OfferGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 8px;

    > * {
      min-width: 82vw;
      scroll-snap-align: start;
    }
  }
`

export const OfferCard = styled.article`
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
`

export const CardImage = styled.img`
  width: 100%;
  height: 180px;
  display: block;
  object-fit: cover;
`

export const OfferBody = styled.div`
  padding: 18px;

  h3 {
    min-height: 44px;
    margin: 8px 0 7px;
    font-size: 17px;
    line-height: 1.3;
  }
`

export const Badge = styled.span`
  display: inline-flex;
  padding: 5px 8px;
  border-radius: 999px;
  background: #f0f4fa;
  color: #40516c;
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
`

export const SmallBadge = styled(Badge)`
  padding: 4px 7px;
  font-size: 8px;
`

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 9px;

  strong {
    color: #e90000;
    font-size: 23px;
  }

  del {
    color: #9ba2ad;
    font-size: 12px;
  }
`

export const Meta = styled.p`
  margin: 7px 0 0 !important;
  color: #66707f !important;
  font-size: 11px !important;
  line-height: 1.45 !important;
`

export const Supplier = styled.div`
  margin-top: 9px;
  color: #303744;
  font-size: 11px;
  font-weight: 700;
`

export const PrimaryLink = styled(Link)`
  min-height: 40px;
  margin-top: 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: #e90000;
  color: #fff;
  font-size: 12px;
  font-weight: 800;

  &:hover {
    color: #fff;
    background: #cc0000;
  }
`

export const PrimaryAnchor = styled.a`
  min-height: 40px;
  margin-top: 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: #e90000;
  color: #fff;
  font-size: 12px;
  font-weight: 800;

  &:hover {
    color: #fff;
    background: #cc0000;
  }
`

export const PartnerBand = styled.div`
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;
`

export const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`

export const PartnerCard = styled.article`
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
`

export const PartnerImage = styled.img`
  width: 100%;
  height: 150px;
  display: block;
  object-fit: cover;
`

export const PartnerContent = styled.div`
  padding: 16px;

  h3 {
    margin: 8px 0 2px;
    font-size: 16px;
  }
`

export const PartnerTop = styled.div`
  min-height: 24px;
  display: flex;
  align-items: center;

  img {
    max-width: 90px;
    max-height: 26px;
    object-fit: contain;
  }

  span {
    color: #677181;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
  }
`

export const PartnerBottom = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 14px;

  strong {
    display: block;
    color: #e90000;
    font-size: 20px;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #8a929f;
    font-size: 9px;
  }
`

export const SecondaryLink = styled(Link)`
  flex: 0 0 auto;
  padding: 10px 13px;
  border-radius: 9px;
  background: #f0f3f7;
  color: #303744;
  font-size: 10px;
  font-weight: 800;

  &:hover {
    color: #e90000;
  }
`

export const SecondaryAnchor = styled.a`
  flex: 0 0 auto;
  padding: 10px 13px;
  border-radius: 9px;
  background: #f0f3f7;
  color: #303744;
  font-size: 10px;
  font-weight: 800;

  &:hover {
    color: #e90000;
  }
`

export const NewsLayout = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 28px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`

export const LeadStory = styled(Link)`
  min-width: 0;
  display: grid;
  grid-template-columns: 44% 1fr;
  gap: 18px;
  color: inherit;

  &:hover {
    color: inherit;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`

export const LeadImage = styled.img`
  width: 100%;
  height: 255px;
  border-radius: 15px;
  object-fit: cover;
`

export const LeadText = styled.div`
  align-self: center;

  h3 {
    margin: 8px 0 10px;
    font-size: 25px;
    line-height: 1.15;
  }

  p {
    margin: 0 0 12px;
    color: #69717e;
    font-size: 13px;
    line-height: 1.6;
  }
`

export const NewsList = styled.div`
  display: flex;
  flex-direction: column;
`

export const NewsItem = styled(Link)`
  min-height: 92px;
  padding: 12px 0;
  border-bottom: 1px solid #e8eaee;
  display: grid;
  grid-template-columns: 122px 1fr;
  gap: 14px;
  color: inherit;

  &:first-child {
    padding-top: 0;
  }

  &:hover {
    color: inherit;
  }

  img {
    width: 122px;
    height: 80px;
    border-radius: 10px;
    object-fit: cover;
  }

  h4 {
    margin: 5px 0;
    font-size: 14px;
    line-height: 1.3;
  }
`

export const NewsMeta = styled.small`
  color: #9299a4;
  font-size: 9px;
`

export const Newsletter = styled.section`
  width: min(1240px, calc(100% - 48px));
  margin: 18px auto 64px;
  padding: 28px 30px;
  border-radius: 18px;
  background: #fff0f0;
  display: grid;
  grid-template-columns: 1fr minmax(320px, 0.8fr);
  align-items: center;
  gap: 34px;

  h2 {
    margin: 5px 0;
    font-size: 21px;
  }

  p {
    margin: 0;
    color: #747c88;
    font-size: 12px;
  }

  @media (max-width: 760px) {
    width: calc(100% - 28px);
    margin-bottom: 36px;
    padding: 22px;
    grid-template-columns: 1fr;
  }
`

export const NewsletterForm = styled.form`
  display: flex;

  input {
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 14px;
    border: 1px solid #e6dede;
    border-radius: 10px 0 0 10px;
    background: #fff;
  }

  button {
    height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 0 10px 10px 0;
    background: #e90000;
    color: #fff;
    font-weight: 800;
  }

  input:disabled,
  button:disabled {
    opacity: 0.75;
  }
`

export const Empty = styled.div`
  padding: 28px;
  border: 1px dashed #dfe3e8;
  border-radius: 14px;
  background: #fafbfc;
  color: #7d8591;
  font-size: 13px;
  text-align: center;
`

export const RetryButton = styled.button`
  display: block;
  margin: 14px auto 0;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  background: #c62a31;
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`
